import classNames from 'classnames';
import styles from './ApplicationDefinition.module.css';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Dropdown,
  MenuProps,
  Space,
  CheckboxProps,
  InputNumber,
  List,
  Spin,
} from 'antd';
import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import Modal from '../../Modal';
import Icon from 'src/components/Icon/Icon';
import { getSearchResultFromQueryParameter } from 'src/app/query.service';
import { MODAL_STATUS } from 'src/app/constants';
import type { FormInstance } from 'antd/es/form';

interface FormData {
  [key: string]: any;
}
// interface FormType {
//   setFieldValue: (field: string, value: any) => void;
// }
interface ApplicationDefinitionProps {
  isActive: boolean;
  onBack: () => void;
  propData: FormData;
  form: FormInstance;
  queryType: string;
  handleNext: (newData: { [key: string]: unknown }) => void;
}

interface Application {
  id: string;
  application: string;
  applicationDescription: string;
  applicationId: string;
}
interface CompletionStatus {
  id: string;
  completionStatus: string;
  applicationCompletionStatus: string;
}
interface DurationMin {
  id: string;
  durationMin: string;
}
interface DurationMax {
  id: string;
  durationMax: string;
}
type SelectedValuesType = Record<string, string[]>;

const ApplicationDefinition = forwardRef<any, ApplicationDefinitionProps>(
  ({ isActive, onBack, propData, form, queryType, handleNext }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isSmallText, setIsSmallText] = useState(false);
    const [minValue, setMinValue] = useState<number>(0);
    const [maxValue, setMaxValue] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<FormData[]>([]);
    const [application, setApplication] = useState<Application[]>([]);
    const [completionStatus, setCompletionStatus] = useState<CompletionStatus[]>([]);
    const [sourceCheck, setSourceCheck] = useState<string>('');
    const [tmpdata, setTmpData] = useState<{ [key: string]: any }>({});
    const [searchValue, setSearchValue] = useState<string>('');
    const inputRef = useRef(null);

    const checkBoxOnChange: CheckboxProps['onChange'] = (e) => {};
    const [selectedValues, setSelectedValues] = useState<{ [key: string]: any }>({
      applicationId: [],
      applicationCompletionStatus: [],
      durationMin: null,
      durationMax: null,
    });

    const onFinish = (values: any) => {};
    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValues({
          applicationId: [],
          applicationCompletionStatus: [],
          durationMin: null,
          durationMax: null,
        });
      },
    }));

    useEffect(() => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        ...propData,
      }));
    }, [propData]);

    const applyMethod = (type: string) => {
      if (type === 'applicationId') {
        form.setFieldValue('applicationId', (selectedValues.applicationId as string[]).join(', '));
      }
      if (type === 'applicationCompletionStatus') {
        form.setFieldValue(
          'applicationCompletionStatus',
          (selectedValues.applicationCompletionStatus as string[]).join(', ')
        );
      }
      const data = { ...selectedValues };
      data.durationMin = minValue;
      data.durationMax = maxValue;
      console.log('minValue...', minValue);
      console.log('data.durationMin...', data.durationMin);
      handleNext(data);
      setTmpData({});
      setSearchValue('');
      setOpenModal(false);
    };

    const handleCancel = () => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [sourceCheck]: [...(tmpdata[sourceCheck] || [])],
      }));
      setSearchValue('');
      setTmpData({});
      setOpenModal(false);
    };

    const handlePlaceholderClick = () => {
      // Focus the input when the placeholder is clicked
      inputRef.current.focus();
    };

    const handleInputClick = async (placeholder: string) => {
      setInputPlaceholder(placeholder);
      setOpenModal(true);
      setSearchValue('');
      form.setFieldValue('searchValue', '');

      switch (placeholder) {
        case 'Application':
          setSourceCheck('applicationId');
          try {
            const searchParameter = 'applicationId';
            setIsLoading(true);
            const appData: Application[] = await getSearchResultFromQueryParameter<Application>(
              queryType,
              searchParameter,
              selectedValues
            );
            setApplication(appData);
            setTmpData({ applicationId: selectedValues.applicationId });
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'Completion Status':
          setSourceCheck('applicationCompletionStatus');
          try {
            const searchParameter = 'applicationCompletionStatus';
            setIsLoading(true);
            const appStatus: CompletionStatus[] =
              await getSearchResultFromQueryParameter<CompletionStatus>(
                queryType,
                searchParameter,
                selectedValues
              );
            setCompletionStatus(appStatus);
            setTmpData({ applicationCompletionStatus: selectedValues.applicationCompletionStatus });
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'Duration Min':
          try {
            const searchParameter = 'durationMin';
            setIsLoading(true);
            const durationMin: DurationMin[] = await getSearchResultFromQueryParameter<DurationMin>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(durationMin);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'Duration Max':
          try {
            const searchParameter = 'durationMax';
            setIsLoading(true);
            const durationMax: DurationMax[] = await getSearchResultFromQueryParameter<DurationMax>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(durationMax);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
      }
    };

    const handleModalInputFocus = () => {
      setIsInputFocused(true);
      setIsSmallText(true);
    };

    const handleModalInputBlur = () => {
      setIsInputFocused(false);
      setIsSmallText(false);
    };

    const onLinkClick = (type: string, value: string) => {
      setSelectedValues((prevValues: SelectedValuesType) => {
        const currentValues = prevValues[type] || [];

        if (!Array.isArray(currentValues)) {
          return prevValues;
        }

        const updatedValues = currentValues.includes(value)
          ? currentValues
          : [...currentValues, value];

        return {
          ...prevValues,
          [type]: updatedValues,
        };
      });
    };
    const handleRemove = (type: string, itemRemove: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.filter((item: string) => item !== itemRemove),
      }));
    };

    const onRangeChange = (type: 'min' | 'max', value: number | null) => {
      const data = { durationMin: 0, durationMax: 0 };
      if (type === 'min') {
        setMinValue(value !== null ? value : 0);
        data.durationMin = value !== null ? value : 0;
        data.durationMax = maxValue;
      } else if (type === 'max') {
        setMaxValue(value !== null ? value : 0);
        data.durationMax = value !== null ? value : 0;
        data.durationMin = minValue;
      }
      handleNext(data);
    };

    const handleMaxClick = () => {
      if (maxValue === 0 || maxValue < minValue) {
        form.setFieldsValue({ durationMax: minValue });
        setMaxValue(minValue);
      }
    };

    const handleInputClear = (type: string) => {
      form.setFieldValue(type, ``);
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: [],
      }));
    };

    const inputFields = [
      'applicationId',
      'applicationCompletionStatus',
      'durationMin',
      'durationMax',
    ];

    const shouldShowResetButton =
      inputFields.some((key) => {
        const value = selectedValues[key];
        return Array.isArray(value) ? value.length > 0 : value !== null;
      }) ||
      minValue > 0 ||
      maxValue > 0;

    const handleReset = () => {
      form.setFieldValue('applicationId', '');
      form.setFieldValue('applicationCompletionStatus', '');
      form.setFieldValue('durationMin', '');
      form.setFieldValue('durationMax', '');
      setMinValue(0);
      setMaxValue(0);
      setSelectedValues({
        applicationId: [],
        applicationCompletionStatus: [],
        durationMin: null,
        durationMax: null,
      });
    };

    const moreItems: MenuProps['items'] = [
      {
        key: '1',
        label: '5',
      },
      {
        key: '2',
        label: '10',
      },
      {
        key: '3',
        label: '15',
      },
      {
        key: '4',
        label: '25',
      },
      {
        key: '5',
        label: '50',
      },
      {
        key: '6',
        label: '70',
      },
    ];
    const filteredSources = application.filter(
      (source) =>
        source.applicationDescription.toLowerCase().includes(searchValue.toLowerCase()) ||
        source.applicationId.toLowerCase().includes(searchValue.toLowerCase())
    );
    const filteredSourcesStatus = completionStatus.filter((source) =>
      source.applicationCompletionStatus.toLowerCase().includes(searchValue.toLowerCase())
    );
    return (
      <div className={classNames(styles.ApplicationDefinition)}>
        <div
          className={`
            ${styles['ApplicationDefinition--QueryTitle']} 
            ${
              isActive
                ? styles['ApplicationDefinition--active']
                : styles['ApplicationDefinition--InActive']
            }
            `}
        >
          Application Definition
          {shouldShowResetButton && (
            <div style={{ display: 'inline', float: 'right', marginRight: '30px' }}>
              <Button
                className={`${
                  isActive
                    ? styles['ApplicationDefinition--Reset']
                    : styles['ApplicationDefinition--InActive']
                }`}
                type="primary"
                htmlType="submit"
                onClick={handleReset}
                disabled={!isActive}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        <div className={classNames(styles['ApplicationDefinition--FormContainer'])}>
          <Form
            form={form}
            name="application_definition_data_form"
            onFinish={onFinish}
            layout="horizontal"
            disabled={!isActive}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="applicationId">
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Application')}
                    placeholder="Application"
                    suffix={
                      selectedValues.applicationId?.length > 0 && (
                        <span onClick={() => handleInputClear('applicationId')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.applicationId?.length > 0 && (
                  <span
                    className={`${styles['ApplicationDefinition--Placeholder']} ${styles.small}`}
                  >
                    Application ({selectedValues.applicationId?.length})
                  </span>
                )}
              </Col>

              <Col span={8}>
                <Form.Item name="applicationCompletionStatus">
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Completion Status')}
                    placeholder="Completion Status"
                    suffix={
                      selectedValues.applicationCompletionStatus?.length > 0 && (
                        <span onClick={() => handleInputClear('applicationCompletionStatus')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.applicationCompletionStatus?.length > 0 && (
                  <span
                    className={`${styles['ApplicationDefinition--Placeholder']} ${styles.small}`}
                  >
                    Completion Status ({selectedValues.applicationCompletionStatus?.length})
                  </span>
                )}
              </Col>

              <Col span={8}>
                <Form.Item name="durationMin">
                  <InputNumber
                    min={0}
                    value={minValue}
                    onChange={(value) => onRangeChange('min', value)}
                    placeholder="Duration Min"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} justify="end">
              <Col span={8}>
                <Form.Item name="durationMax">
                  <InputNumber
                    min={0}
                    value={maxValue}
                    onChange={(value) => onRangeChange('max', value)}
                    onClick={handleMaxClick}
                    placeholder="Duration Max"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {isActive && (
              <Row justify="space-between">
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['ApplicationDefinition--Buttons'])}
                      type="primary"
                      onClick={onBack}
                    >
                      Back
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Modal open={openModal} onClose={() => setOpenModal(false)} width={900} footer={null}>
              <div className={classNames(styles['ApplicationDefinition--ModalHeader'])}>
                Select {inputPlaceholder}
                {inputPlaceholder === 'Status' && <span> - {MODAL_STATUS}</span>}
              </div>
              <div className={classNames(styles['ApplicationDefinition--ModelContent'])}>
                <div>
                  <p className={classNames(styles['ApplicationDefinition--TagTitle'])}>Selection</p>
                  <div className={classNames(styles['ApplicationDefinition--TagsContainer'])}>
                    {(selectedValues[sourceCheck] || []).map((item: any, index: any) => {
                      return (
                        <Button
                          type="primary"
                          key={`${item}${index}`}
                          onClick={() => handleRemove(sourceCheck, item)}
                          className={styles['selected-button']}
                        >
                          {item}
                          <Icon
                            name="close"
                            className={`${styles['ApplicationnDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                          />
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div className={styles['ApplicationDefinition--Search']}>
                  <Row gutter={16} justify="space-between">
                    <Col span={8}>
                      <div style={{ position: 'relative' }}>
                        <Input
                          name="filterValue"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onFocus={handleModalInputFocus}
                          onBlur={handleModalInputBlur}
                          ref={inputRef} // Use ref to access the input
                        />
                        <span
                          className={`${styles['ApplicationDefinition--ModalInputText']} ${
                            isSmallText ? styles.small : ''
                          }`}
                          onClick={handlePlaceholderClick} // Click handler to focus the input
                        >
                          Look for {inputPlaceholder}
                        </span>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                        <Button className={styles['ApplicationDefinition--ModalDropDown']}>
                          Limit
                          <Icon
                            name="arrow_drop_down"
                            className={styles['ApplicationDefinition--ModalDropDownIcon']}
                          />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </div>
                <div className={styles['ApplicationDefinition--SearchList']}>
                  {sourceCheck === 'applicationId' && (
                    <List
                      itemLayout="horizontal"
                      // loading={isLoading}
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredSources}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.applicationId.includes(item.applicationId)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('applicationId', item.applicationId)}
                        >
                          <span>{item.applicationId}</span>
                          <span>{item.applicationDescription}</span>
                        </List.Item>
                      )}
                    />
                  )}
                  {sourceCheck === 'applicationCompletionStatus' && (
                    <List
                      itemLayout="horizontal"
                      // loading={isLoading}
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredSourcesStatus}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.applicationCompletionStatus.includes(
                              item.applicationCompletionStatus
                            )
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() =>
                            onLinkClick(
                              'applicationCompletionStatus',
                              item.applicationCompletionStatus
                            )
                          }
                        >
                          <span>{item.applicationCompletionStatus}</span>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
                <div className={styles['ApplicationDefinition--ModalPageNo']}> 1 - 4 / 4 </div>
              </div>
              <div className={styles['ApplicationDefinition--ModalFooter']}>
                <Space>
                  <Button
                    onClick={handleCancel}
                    className={styles['ApplicationDefinition--CancelButton']}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => applyMethod(sourceCheck)}
                    className={styles['ApplicationDefinition--Buttons']}
                  >
                    Apply
                  </Button>
                </Space>
              </div>
            </Modal>
          </Form>
        </div>
      </div>
    );
  }
);
ApplicationDefinition.displayName = 'ApplicationDefinition';
export default ApplicationDefinition;
